import type { ReceiptView } from 'shared-models/browser';
import { roundMoney } from 'shared-models/browser';

// Splits a multi-payment receipt into one receipt per payment line (each its own fiscal copy).
// Each copy's totals are recomputed for that payment slice so the VAT split matches its own amount
// (carrying the full-order totals would print the whole-order VAT split next to a single payment).
export function splitReceiptByPayment(receipt: ReceiptView): ReceiptView[] {
  if (receipt.payments.length <= 1) {
    return [receipt];
  }
  const rate = receipt.totals.vatRatePercent;
  return receipt.payments.map((payment, index) => {
    const baseAmount = payment.baseAmount ?? payment.amount;
    const tipAmount = payment.tipAmount ?? 0;
    const exVatTotal = roundMoney(rate > 0 ? baseAmount / (1 + rate / 100) : baseAmount);
    return {
      ...receipt,
      payments: [payment],
      isPartial: true,
      paymentIndex: index + 1,
      paymentCount: receipt.payments.length,
      totals: {
        ...receipt.totals,
        exVatTotal,
        vatAmount: roundMoney(baseAmount - exVatTotal),
        billableTotal: baseAmount,
        tipAmount,
        paidAmount: payment.amount,
        changeAmount: 0,
      },
    };
  });
}

export type ReceiptPrintJob = {
  id: string;
  receipt: ReceiptView;
  title?: string;
  showPaymentDetails?: boolean;
};

// Print list: N partial copies, plus one full "חשבון" summary when paid via >1 payment.
export function receiptsForPrinting(receipt: ReceiptView): ReceiptPrintJob[] {
  const jobs: ReceiptPrintJob[] = splitReceiptByPayment(receipt).map((r, index) => ({
    id: r.payments[0] ? `payment:${r.payments[0].paymentlineId}` : `payment:${index}`,
    receipt: r,
  }));
  if (receipt.payments.length > 1) {
    jobs.push({ id: 'final', receipt, title: 'חשבון', showPaymentDetails: false });
  }
  return jobs;
}
